import { Injectable } from '@angular/core';
import { Team } from '@app/models/team';
import { AuthService } from '@app/services/auth.service';
import { TeamService } from '@app/services/team.service';
import { User } from '@app/models/user';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment'
import { Define } from '@app/models/define';
import { Firestore } from '@angular/fire/firestore';
import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  DocumentReference,
  endAt,
  getDoc,
  getDocs,
  orderBy,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  startAt,
  setDoc,
  where
} from 'firebase/firestore';

export class UserEx implements User {
  uid: string;
  email: string;
  displayName?: string;
  kana?: string;
  key?: string;
  id?: string;
  team?: DocumentReference<DocumentData>;
  authority?: number;
  manageAuthority?: number;
  teamName?: string;
  withoutYakusya?: boolean;
  bugyoParallelOperation?: boolean;
  closingDay?: number;
  passwordChanged?: boolean;
  approverUid?: string;
}


@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private http: HttpClient,
    private teamService: TeamService,
    private firestore: Firestore,
    public auth: AuthService
  ) {
  }

  private usersCollection = collection(this.firestore, 'users');
  private teamCollection = collection(this.firestore, 'team');

  private async resolveTeamName(
    teamRef: DocumentReference<DocumentData> | undefined | null,
    notSetLabel: string
  ): Promise<string> {
    if (!teamRef) {
      return notSetLabel;
    }
    const snapshot = await getDoc(teamRef);
    if (!snapshot.exists()) {
      return 'Team Not Find.';
    }
    const teamData = snapshot.data() as Team | undefined;
    return teamData?.name ?? '';
  }

  private async mapUser(
    docSnap: QueryDocumentSnapshot<DocumentData>,
    notSetLabel: string
  ): Promise<UserEx> {
    const data = docSnap.data() as UserEx;
    const user: UserEx = { ...data };
    if (user.kana === undefined || user.kana === null) {
      user.kana = '';
    }
    user.teamName = await this.resolveTeamName(user.team ?? null, notSetLabel);
    return user;
  }

  async getUsers(): Promise<UserEx[]> {
    const snapshot = await getDocs(this.usersCollection);
    return Promise.all(snapshot.docs.map(docSnap => this.mapUser(docSnap, 'Team Not Set.')));
  }

  async getFilteredUsers(auth: number): Promise<UserEx[]> {
    const usersQuery = query(this.usersCollection, where('authority', '==', auth));
    const snapshot = await getDocs(usersQuery);
    const result = await Promise.all(snapshot.docs.map(docSnap => this.mapUser(docSnap, '')));
    result.sort( (a, b) => {
      if (a.teamName > b.teamName) {
        return 1;
      }
      if (a.teamName < b.teamName) {
        return -1;
      }
      return 0;
    });
    return result;
  }

  async getUsersWithFilter(targetNum: number, filterValue: string): Promise<UserEx[]> {
    let snapshot: QuerySnapshot<DocumentData> | undefined;
    if (filterValue) {
      switch (targetNum) {
        case Define.UserField.Id:
          snapshot = await getDocs(query(this.usersCollection, where('id', '==', filterValue)));
          break;
        case Define.UserField.Name:
          // firestoreでは前方一致検索が限界
          snapshot = await getDocs(
            query(
              this.usersCollection,
              orderBy('displayName'),
              startAt(filterValue),
              endAt(filterValue + '\uf8ff')
            )
          );
          break;
        case Define.UserField.Kana:
          // firestoreでは前方一致検索が限界
          snapshot = await getDocs(
            query(
              this.usersCollection,
              orderBy('kana'),
              startAt(filterValue),
              endAt(filterValue + '\uf8ff')
            )
          );
          break;
        case Define.UserField.Team:
          const teamRef = await this.teamService.getTeamRef(filterValue);
          snapshot = await getDocs(query(this.usersCollection, where('team', '==', teamRef)));
          break;
        default:
      }
    } else {
      // 空文字で検索の時は全件を返す
      snapshot = await getDocs(this.usersCollection);
    }
    if (snapshot === undefined) {
      return Promise.all([]);
    }
    return Promise.all(snapshot.docs.map(docSnap => this.mapUser(docSnap, 'Team Not Set.')));
  }

  createUser(data: User, password: string) {
    return new Promise<boolean>(async (resolve, reject) => {
      // 認証トークンをヘッダーに入れる。
      const token = await this.auth.getToken();
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'authorization': 'Bearer ' + token
        })
      };
      try {
        // 送信内容を組み立てる。
        const body = { email: data.email, password: password };
        // POSTする。
        console.log('post create user');
        const uri = environment.setting.functionUri + '/createUser';
        // console.log(uri);
        this.http.post(
          uri,
          body,
          httpOptions).subscribe(async response => {
            console.log('post response');
            console.log(response);
            data.uid = response['uid'];
            data.passwordChanged = false;
            const userDocRef = doc(this.firestore, 'users', response['uid']);
            await setDoc(userDocRef, data);
            resolve(true);
          }, _error => resolve(false));
      } catch (err) {
        console.error('post create user error', err);

        resolve(false);
      }
    });
  }



  updateUser(data: User) {
    return new Promise<boolean>(async (resolve, reject) => {
      // 認証トークンをヘッダーに入れる。
      const token = await this.auth.getToken();
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'authorization': 'Bearer ' + token
        })
      };
      try {
        // 送信内容を組み立てる。
        const body = { uid: data.uid, email: data.email };
        // POSTする。
        console.log('post update user:' + data.uid);
        this.http.post(
          environment.setting.functionUri + '/updateUser',
          body,
          httpOptions).subscribe(async response => {
            console.log('post response');
            console.log(response);
            // data.uid = response['uid'];
            await setDoc(doc(this.firestore, 'users', data.uid), data);
            resolve(true);
          }, _error => resolve(false));
      } catch (err) {
        console.error('post update user error', err);

        resolve(false);
      }
    });
  }

  async delete(data: User) {
    // TODO functionでユーザー削除
    return new Promise<void>(async (resolve, reject) => {
      // 認証トークンをヘッダーに入れる。
      const token = await this.auth.getToken();
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'authorization': 'Bearer ' + token
        })
      };
      // 送信内容を組み立てる。
      const body = { uid: data.uid };
      // POSTする。
      console.log('post');
      this.http.post(
        environment.setting.functionUri + '/deleteUser',
        body,
        httpOptions).subscribe(async response => {
          console.log('post response');
          console.log(response);
          await deleteDoc(doc(this.firestore, 'users', data.uid));

          resolve();
        });
    });
  }

  async resetPassword(data: User): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      // 認証トークンをヘッダーに入れる。
      const token = await this.auth.getToken();
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'authorization': 'Bearer ' + token
        })
      };
      // 送信内容を組み立てる。
      const body = { uid: data.uid };
      // POSTする。
      console.log('post');
      this.http.post(
        environment.setting.functionUri + '/resetPassword',
        body,
        httpOptions).subscribe(async response => {
          console.log('post response');
          // console.log(response);
          const newPassword: string = response['newPassword'];

          const userRef = doc(this.firestore, 'users', data.uid);
          await setDoc(userRef, { passwordChanged: false }, { merge: true });

          resolve(newPassword);
        });
    });
  }

  async getCurrentAuth() {
    return (await this.auth.getUser()).authority;
  }
  async getCurrentManageAuth() {
    return (await this.auth.getUser()).manageAuthority;
  }

  async isAdminManageAuth() {
    const auth = (await this.auth.getUser()).manageAuthority;
    return  (auth === Define.ManageAuthority.Administrator);
  }

  async isUniqueKeyId(data: User): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      const users = await this.getUsers();
      const user = users.find((u) => {
        return u.key === data.key && u.uid !== data.uid;
      });
      if (user !== undefined) {
        console.log('isUniqueKeyId not unique %s', data.key);
        resolve(false);
      } else {
        console.log('isUniqueKeyId unique %s', data.key);

        resolve(true);
      }
    });
  }

  async isUniqueEmail(email: string): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      const users = await this.getUsers();
      const user = users.find((u) => {
        return u.email === email;
      });
      if (user !== undefined) {
        console.log('isUniqueEmail not unique');
        resolve(false);
      } else {
        console.log('isUniqueEmail unique');
        resolve(true);
      }
    });
  }

  isValidEmail(email: string): boolean {
    const regexpEmail = new RegExp(/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/);
    const match =  regexpEmail.test(email);
    return match;
  }

  isValidKana(kana: string): boolean {
    const regexpKana = new RegExp(/^[ぁ-んー　゛]*$/);
    const match = regexpKana.test(kana);
    return match;
  }
}
